import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, ArrowLeft, Plus, Trash2, Save, X, Edit2, Tags as TagsIcon, Sparkles, Star, Image as ImageIcon, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCaseLibrary = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleBackfillThumbnails = async () => {
    if (!window.confirm('回填封面圖將自動 fetch 每個案例嘅 Vimeo 封面（缺失嘅先處理）。繼續？')) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/case-library/admin/backfill-thumbnails`,
        {},
        { timeout: 120000 },
      );
      const msg = `完成：共 ${data.total_scanned} 個案例，缺失 ${data.total_missing}，成功 ${data.updated}，失敗 ${data.failed}`;
      if (data.failed > 0) {
        toast.warning(msg);
      } else {
        toast.success(msg);
      }
      await fetchData();
    } catch (err) {
      toast.error('回填失敗：' + (err.response?.data?.detail || err.message));
    } finally {
      setBackfilling(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/case-library/admin/export-cases`,
        { responseType: 'blob', timeout: 60000 },
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      a.download = `case-library-${ts}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel 已下載');
    } catch (err) {
      toast.error('匯出失敗：' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    vimeo_embed_url: '',
    analysis_content: '',
    original_video_url: '',
    industry_tags: [],
    video_type_tags: [],
    platform_tags: [],
    status: 'published'
  });

  const editorOptions = useMemo(() => ({
    spellChecker: false,
    placeholder: '請輸入詳細的拆解分析...',
    status: false,
    toolbar: ['bold', 'italic', 'heading', '|', 'quote', 'unordered-list', 'ordered-list', '|', 'link', 'preview']
  }), []);

  useEffect(() => {
    // Wait for AuthContext to finish loading before deciding to redirect.
    // Without this guard, a hard-refresh on /admin/case-library redirects
    // to /create because `user` is null on first render.
    if (user === undefined || user === null) return;
    if (user.role !== 'admin' && user.role !== 'operator') {
      navigate('/create');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [casesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/case-library/admin/cases`),
        axios.get(`${API_URL}/api/case-library/admin/categories`)
      ]);
      setCases(casesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('獲取數據失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title || !formData.vimeo_embed_url) {
      toast.error('請填寫標題和 Vimeo 連結');
      return;
    }

    // 自動轉換 Vimeo 連結
    let embedUrl = formData.vimeo_embed_url.trim();
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const match = embedUrl.match(vimeoRegex);
    
    if (match) {
      const videoId = match[1];
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
      console.log('自動轉換 Vimeo 連結:', embedUrl);
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/case-library/admin/cases`, {
        ...formData,
        vimeo_embed_url: embedUrl
      });
      toast.success('案例創建成功！');
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('創建失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    // 自動轉換 Vimeo 連結
    let embedUrl = formData.vimeo_embed_url.trim();
    const vimeoRegex = /vimeo\.com\/(\d+)/;
    const match = embedUrl.match(vimeoRegex);
    
    if (match) {
      const videoId = match[1];
      embedUrl = `https://player.vimeo.com/video/${videoId}`;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/case-library/admin/cases/${editingCase}`, {
        ...formData,
        vimeo_embed_url: embedUrl
      });
      toast.success('更新成功！');
      setEditingCase(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('更新失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`確定要刪除「${title}」嗎？`)) return;

    try {
      await axios.delete(`${API_URL}/api/case-library/admin/cases/${id}`);
      toast.success('刪除成功！');
      fetchData();
    } catch (error) {
      toast.error('刪除失敗');
    }
  };

  const startEdit = (caseItem) => {
    setEditingCase(caseItem.id);
    setFormData({
      title: caseItem.title,
      description: caseItem.description,
      vimeo_embed_url: caseItem.vimeo_embed_url,
      analysis_content: caseItem.analysis_content,
      original_video_url: caseItem.original_video_url || '',
      industry_tags: caseItem.industry_tags || [],
      video_type_tags: caseItem.video_type_tags || [],
      platform_tags: caseItem.platform_tags || [],
      status: caseItem.status
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      vimeo_embed_url: '',
      analysis_content: '',
      original_video_url: '',
      industry_tags: [],
      video_type_tags: [],
      platform_tags: [],
      status: 'published'
    });
  };

  const toggleTag = (type, tag) => {
    const key = `${type}_tags`;
    const current = formData[key];
    if (current.includes(tag)) {
      setFormData({ ...formData, [key]: current.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, [key]: [...current, tag] });
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'operator') return null;

  return (
    <>
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 px-8 py-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900">案例管理</h1>
            <p className="text-neutral-600 text-sm mt-1">管理影片案例和拆解分析</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/case-categories')} className="rounded-full">
              <TagsIcon className="w-4 h-4 mr-2" />
              分類管理
            </Button>
            <Button
              variant="outline"
              onClick={handleBackfillThumbnails}
              disabled={backfilling}
              className="rounded-full"
              data-testid="admin-case-backfill-thumbnails-btn"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              {backfilling ? '回填中…' : '回填封面圖'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={exporting}
              className="rounded-full"
              data-testid="admin-case-export-excel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? '匯出中…' : '下載 Excel'}
            </Button>
            <Button onClick={() => setShowCreateForm(true)} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              新增案例
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto p-8">
        {(showCreateForm || editingCase) && (
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold">{editingCase ? '編輯案例' : '新增案例'}</h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowCreateForm(false); setEditingCase(null); resetForm(); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <CaseForm
              formData={formData}
              setFormData={setFormData}
              categories={categories}
              toggleTag={toggleTag}
              onSave={editingCase ? handleUpdate : handleCreate}
              onCancel={() => { setShowCreateForm(false); setEditingCase(null); resetForm(); }}
              saving={saving}
              editorOptions={editorOptions}
            />
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-neutral-600">載入中...</p>
          </div>
        ) : cases.length === 0 && !showCreateForm ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">尚無案例</h3>
            <p className="text-neutral-600 mb-6">創建您的第一個案例</p>
            <Button onClick={() => setShowCreateForm(true)} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              新增案例
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cases.map((caseItem) => (
              <Card key={caseItem.id} className="overflow-hidden">
                {editingCase === caseItem.id ? null : (
                  <>
                    <div className="aspect-video bg-neutral-900 relative">
                      <iframe
                        src={caseItem.vimeo_embed_url}
                        className="w-full h-full"
                        frameBorder="0"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-neutral-900 mb-2 line-clamp-1">{caseItem.title}</h3>
                      <p className="text-sm text-neutral-600 mb-3 line-clamp-2">{caseItem.description}</p>
                      
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {caseItem.industry_tags?.slice(0, 2).map((tag, i) => (
                          <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await axios.patch(
                                `${API_URL}/api/case-library/admin/cases/${caseItem.id}/featured`,
                                { is_featured: !caseItem.is_featured, featured_order: caseItem.featured_order || 0 }
                              );
                              fetchData();
                              toast.success(caseItem.is_featured ? '已取消精選' : '已加入精選');
                            } catch (e) {
                              toast.error('操作失敗');
                            }
                          }}
                          className={`rounded-lg ${caseItem.is_featured ? 'border-lime-500 text-atelier-coral bg-atelier-coral/10' : ''}`}
                          data-testid={`toggle-featured-${caseItem.id}`}
                          title={caseItem.is_featured ? '取消精選' : '加入精選'}
                        >
                          <Star className="w-3 h-3" fill={caseItem.is_featured ? 'currentColor' : 'none'} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => startEdit(caseItem)} className="flex-1 rounded-lg">
                          <Edit2 className="w-3 h-3 mr-1" />
                          編輯
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(caseItem.id, caseItem.title)} className="text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>
    </>
  );
};

const CaseForm = ({ formData, setFormData, categories, toggleTag, onSave, onCancel, saving, editorOptions }) => {
  const getCategoriesByType = (type) => categories.filter(c => c.type === type);
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, []);

  const handleAutoAnalysis = async () => {
    if (!videoUrl) {
      toast.error('請輸入影片連結');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(`${API_URL}/api/case-library/auto-analyze`, {
        video_url: videoUrl
      });

      const jobId = response.data.job_id;
      const pollJob = async () => {
        try {
          const jobResponse = await axios.get(`${API_URL}/api/case-library/auto-analyze/${jobId}`);
          const job = jobResponse.data;

          if (job.status === 'completed') {
            setFormData(prev => ({
              ...prev,
              analysis_content: job.analysis || ''
            }));
            toast.success('自動分析完成！');
            setShowAnalysisDialog(false);
            setVideoUrl('');
            setAnalyzing(false);
            return;
          }

          if (job.status === 'failed') {
            toast.error(job.error || '分析失敗，請稍後再試');
            setAnalyzing(false);
            return;
          }

          pollingRef.current = setTimeout(pollJob, 4000);
        } catch (error) {
          console.error('分析輪詢失敗:', error);
          toast.error('分析失敗，請稍後再試');
          setAnalyzing(false);
        }
      };

      pollJob();
    } catch (error) {
      console.error('分析失敗:', error);
      toast.error(error.response?.data?.detail || '分析失敗，請稍後再試');
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label>標題 *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="例：開場3秒抓住眼球的技巧"
          />
        </div>
        <div>
          <Label>狀態</Label>
          <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
            <SelectTrigger className="bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="published" className="bg-white hover:bg-green-50">已發布</SelectItem>
              <SelectItem value="draft" className="bg-white hover:bg-yellow-50">草稿</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>簡介</Label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="簡短描述這個案例..."
          className="min-h-[80px]"
        />
      </div>

      <div>
        <Label>Vimeo 連結 *</Label>
        <Input
          value={formData.vimeo_embed_url}
          onChange={(e) => setFormData({ ...formData, vimeo_embed_url: e.target.value })}
          placeholder="https://vimeo.com/123456789 或 https://player.vimeo.com/video/123456789"
        />
        <p className="text-xs text-neutral-500 mt-1">
          支持兩種格式，系統會自動轉換為嵌入連結
        </p>
        <p className="text-xs text-amber-600 mt-1">
          ⚠️ 注意：影片必須設定為「公開」或「任何人都可以觀看」
        </p>
      </div>

      <div>
        <Label>原影片連結（選填）</Label>
        <Input
          value={formData.original_video_url}
          onChange={(e) => setFormData({ ...formData, original_video_url: e.target.value })}
          placeholder="https://www.douyin.com/... 或 https://www.instagram.com/..."
        />
        <p className="text-xs text-neutral-500 mt-1">抖音、Instagram、TikTok 等平台的原始連結</p>
      </div>

      <div className="space-y-4 border-t pt-4">
        <h3 className="font-medium text-neutral-900">分類標籤</h3>
        
        <div>
          <Label className="text-sm">行業</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {getCategoriesByType('industry').map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleTag('industry', cat.name)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  formData.industry_tags.includes(cat.name)
                    ? 'bg-blue-500 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-sm">影片類別</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {getCategoriesByType('video_type').map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleTag('video_type', cat.name)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  formData.video_type_tags.includes(cat.name)
                    ? 'bg-green-500 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-sm">平台</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {getCategoriesByType('platform').map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => toggleTag('platform', cat.name)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  formData.platform_tags.includes(cat.name)
                    ? 'bg-purple-500 text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>拆解分析內容</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAnalysisDialog(true)}
            className="rounded-lg"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            自動分析
          </Button>
        </div>
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <SimpleMDE
            value={formData.analysis_content}
            onChange={(value) => setFormData({ ...formData, analysis_content: value })}
            options={editorOptions}
          />
        </div>
        <p className="text-xs text-neutral-500 mt-1">支持 Markdown 格式</p>
      </div>

      {/* Auto Analysis Dialog */}
      {showAnalysisDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAnalysisDialog(false)}>
          <Card className="p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-heading font-bold text-neutral-900">自動影片分析</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowAnalysisDialog(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <Label>影片連結</Label>
                <Input
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="貼上 Instagram Reels / YouTube Shorts / 抖音 / TikTok 連結"
                  className="mt-1"
                />
                <p className="text-xs text-neutral-500 mt-1">
                  支持：Instagram Reels、YouTube Shorts、抖音、TikTok
                </p>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={handleAutoAnalysis}
                  disabled={analyzing || !videoUrl}
                  className="flex-1 rounded-lg"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {analyzing ? '分析中...' : '開始分析'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {setShowAnalysisDialog(false); setVideoUrl('');}}
                  className="rounded-lg"
                >
                  取消
                </Button>
              </div>

              {analyzing && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-neutral-600">正在提取和分析影片內容...</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      <div className="flex gap-2 pt-4 border-t">
        <Button onClick={onSave} disabled={saving} className="rounded-full">
          <Save className="w-4 h-4 mr-2" />
          {saving ? '儲存中...' : '儲存'}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">
          取消
        </Button>
      </div>
    </div>
  );
};

export default AdminCaseLibrary;
